export interface BaseProduct {
  meta: IProductDataMeta;
  subscription: IProductDataSubscription;
  userService: IProductDataUserService;
  addon: {
    is_enabled: boolean;
  };
}

export interface ModifyMenuProduct extends BaseProduct {
  addon: ModifyMenuDataAddon;
}

export interface LiveTranslationsProduct extends BaseProduct {
  addon: LiveTranslationsDataAddon;
}

export interface ImageProduct extends BaseProduct {
  addon: ImagesDataAddon;
}

export interface PdfAutoRemediationProduct {
  addon: PdfDataAddon;
  userService: IProductDataUserService;
  subscription: {providerSubscription: StripeSubscription; userwaySubscription: StripeSubscription};
}

export interface StatsProduct extends BaseProduct {
  addon: StatsDataAddon | null;
}

interface BasesDataAddon {
  id: number;
  is_enabled: boolean;
  is_renewable: boolean;
  user_service_id: number;
}

export interface ImagesDataAddon extends BasesDataAddon {
  created_at: string;
  images_quota: number;
  pricing_tier: string;
  updated_at: string;
}

export interface PdfDataAddon extends BasesDataAddon {
  created_at: string;
  total_quota: number;
  pricing_tier: string;
  updated_at: string;
}
export interface StatsDataAddon extends BasesDataAddon {
  stats: IProductDataAddonStats;
}

export interface LiveTranslationsDataAddon extends BasesDataAddon {
  auto_renew_on_quota_exceeded: boolean;
  auto_translation: boolean;
  live_translation_quota: number;
  pricing_tier: string;
}

export interface ModifyMenuDataAddon extends BasesDataAddon {
  color_mode: string;
  dictionary: boolean;
  features_pattern: string;
  oversized: boolean;
  profiles: boolean;
  two_columns_layout: boolean;
}

type IProductDataMeta = {
  sites: IProductDataMetaSite[];
  trialApplicable: boolean;
  cost?: number;
  discount?: number;
  paidAsAgency?: boolean;
  trialDaysLeft?: number;
};

type IProductDataSubscription = {
  id: number;
  payment_provider_sub_id: string;
  status: string;
  stripeSubscription: StripeSubscription;
};

type StripeSubscription = {
  application: string;
  application_fee_percent: string;
  automatic_tax: {
    enabled: boolean;
  };
  billing_cycle_anchor: string;
  billing_thresholds: string;
  cancel_at: string;
  cancel_at_period_end: boolean;
  canceled_at: string;
  collection_method: string;
  created: number;
  currency: string;
  current_period_end: number;
  current_period_start: number;
  customer: string;
  days_until_due: string;
  default_payment_method: string;
  default_source: string;
  default_tax_rates: [any];
  description: string;
  discount: {
    checkout_session: string;
    coupon: IProductDataSubscriptionCouponSite;
    customer: string;
    end: string;
    id: string;
    invoice: string;
    invoice_item: string;
    object: string;
    promotion_code: string;
    start: string;
    subscription: string;
  };
  ended_at: string;
  id: string;
  items: any;
  latest_invoice: any;
  livemode: boolean;
  metadata: any;
  next_pending_invoice_item_invoice: string;
  object: string;
  on_behalf_of: string;
  pause_collection: string;
  payment_settings: {
    payment_method_options: string;
    payment_method_types: string;
    save_default_payment_method: string;
  };
  pending_invoice_item_interval: string;
  pending_setup_intent: string;
  pending_update: string;
  plan: IStripeSubscriptionProduct;
  quantity: number;
  schedule: string;
  start_date: string;
  status: string;
  test_clock: string;
  transfer_data: string;
  trial_end: string;
  trial_start: string;
};

export interface IProductDataUserService {
  billing_cycle_months: string;
  billingCycleMonths?: number;
  comment: string;
  created_at: string;
  expires_at: string;
  expiresAt?: string;
  id: number;
  is_trial: boolean;
  managed_subscription: boolean;
  starts_at: string;
  status: string;
  subject_transaction_type: string;
  subscription_id: number;
  subscriptionId?: number;
  updated_at: string;
  user_id: number;
}

type IProductDataMetaSite = {
  host: string;
  id: number;
};

type IProductDataSubscriptionCouponSite = {
  amount_off: string;
  created: number;
  currency: string;
  duration: string;
  duration_in_months: string;
  id: string;
  livemode: boolean;
  max_redemptions: boolean;
  metadata: any;
  name: string;
  object: string;
  percent_off: number;
  redeem_by: string;
  times_redeemed: number;
  valid: boolean;
};

type IStripeSubscriptionProduct = {
  interval: string;
  active: boolean;
  attributes: [any];
  created: string;
  default_price: string;
  description: string;
  id: string;
  images: [any];
  livemode: boolean;
  metadata: any;
  name: string;
  object: string;
  package_dimensions: string;
  shippable: string;
  statement_descriptor: string;
  tax_code: string;
  type: string;
  unit_label: string;
  updated: number;
  url: string;
  product?: {id: string};
};

export type IProductDataAddonStats = {
  NINETY: IProductStatsByPeriod;
  SEVEN: IProductStatsByPeriod;
  SIXTY: IProductStatsByPeriod;
  THIRTY: IProductStatsByPeriod;
  expires_at: string;
  id: number;
  is_enabled: boolean;
  is_renewable: boolean;
  is_trial: boolean;
  trial_applicable: boolean;
  user_service_id: number;
};

export type IProductStatsByPeriod = {
  features: IProductStatsFeatures[];
  featuresByFunction?: IProductStatsFeatures[];
  featuresByProfile?: IProductStatsFeatures[];
  forDays: number;
  hides: number;
  loads: number;
  opens: number;
  percentage: string;
  loadsHistory?: {date: string; value: number}[];
  opensHistory?: {date: string; value: number}[];
};

export type IProductStatsFeatures = {
  name: string;
  percents?: string | number;
  value?: number;
  icon?: string;
};

export type IProductDataAddonModifyMenu = {
  id?: number;
  user_service_id?: number;
  is_enabled?: boolean;
  is_renewable?: boolean;
  features_pattern?: string;
  profiles?: boolean;
  two_columns_layout?: boolean;
  oversized?: boolean;
  allSitesNumber?: number;
  trialApplicable?: boolean;
  modifyMenuSites?: IProductDataMeta['sites'];
};

export type BaseProducts = {
  options: {
    widgetVisible: boolean;
  };
  products: IProducts;
  addon: IProductDataAddon;
  meta: IProductDataMeta;
  subscription: IProductDataSubscription;
  userService: IProductDataUserService;
};

export type IProducts = {
  WHITE_LABEL: BaseProduct;
  CUSTOM_BRANDING: BaseProduct;
  MODIFY_MENU: BaseProduct;
  USAGE_STATS: BaseProduct;
  LIVE_TRANSLATIONS: BaseProduct;
  REMEDIATION: BaseProduct;
};

export type IProductsAgencyConfig = {
  key: string;
  enabled: boolean;
  disabled: boolean;
  cost: number;
  discount: number;
  startsAt: string;
  expiresAt: string;
  agencyFree: boolean;
  paidAsAgency: boolean;
  loading?: boolean;
};

type IProductDataAddon = {
  aria_editor_enabled: boolean;
  aria_editor_values_path: string;
  broken_links_enabled: boolean;
  complex_el_rem_config: {
    navigationMenu: {
      enable: boolean;
    };
    skipLinks: {
      enable: boolean;
      anchorSelector: boolean;
    };
  };
  content_moderator_enabled: boolean;
  contrast_rem_config: {
    enabled: boolean;
    autofix: boolean;
    path: string;
  };
  custom_focus_enabled: boolean;
  custom_focus_style: string;
  custom_script_enabled: boolean;
  custom_script_file_key: string;
  custom_script_file_path: string;
  empty_controls_enabled: boolean;
  empty_controls_path: string;
  fixes_headers_file_path: string;
  fixes_identical_targets_path: string;
  fixes_img_alt_file_path: string;
  fixes_vague_links_file_path: string;
  forms_remediation_enabled: boolean;
  headers_remediation_enabled: boolean;
  id: number;
  identical_targets_enabled: boolean;
  img_ai_processed_number: number;
  img_alt_remediation_enabled: boolean;
  is_enabled: boolean;
  is_renewable: boolean;
  language_enabled: boolean;
  language_path: string;
  pdf_files_processed_number: number;
  pfd_processing_enabled: boolean;
  rem_strategy: string;
  resources_file_key: string;
  resources_file_path: string;
  user_service_id: number;
  vague_links_remediation_enabled: boolean;
  stats?: IProductDataAddonStats;
};

export type ProductsDetailsAgency = {
  options: AgencySiteOptions;
  productsMap: ProductsAgency;
};

type AgencySiteOptions = {
  widgetVisible: boolean;
};

export type ProductsAgency = {
  WHITE_LABEL: BaseProductAgency;
  CUSTOM_BRANDING: BaseProductAgency;
  MODIFY_MENU: BaseProductAgency;
  USAGE_STATS: BaseProductAgency;
  LIVE_TRANSLATIONS: BaseProductAgency;
  REMEDIATION: BaseProductAgency;
};

export type BaseProductAgency = {
  agencyState: 'FREE' | 'PAID' | null;
  isEnabled: boolean;
  expiresAt: string;
  startsAt: string;
  totals: {
    discountPercentage: number;
    discountUsd: number;
    subtotalUsd: number;
    taxRate: number;
    taxUsd: number;
    totalUsd: number;
  };
};

export enum AgencyState {
  FREE = 'FREE',
  PAID = 'PAID',
}
